import { useState } from "react";
import { toast } from "sonner";
import { Banknote, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PaymentAPI, extractError } from "@/lib/api";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;
const METHODS = ["cash", "upi", "cheque", "bank_transfer"];

/**
 * PaymentDialog — manual payment recording (cash/UPI/cheque/bank).
 * Server enforces `payment:create` permission.
 */
export function PaymentDialog({ invoice, onPaid, triggerLabel = "Record Payment", trigger, testid = "record-payment-btn" }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState(invoice?.amount_due_minor || 0);
    const [method, setMethod] = useState("cash");
    const [reference, setReference] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!invoice) return;
        if (!amount || Number(amount) <= 0) { toast.error("Amount must be > 0"); return; }
        setBusy(true);
        try {
            const r = await PaymentAPI.create({
                invoice_id: invoice.id, branch_id: invoice.branch_id,
                amount_minor: Number(amount), method, reference: reference || null,
                notes: notes || null,
            });
            toast.success(`Recorded payment ${r.payment_number || ""}`);
            setOpen(false);
            onPaid?.(r);
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button data-testid={testid} className="bg-brand-red text-white hover:bg-brand-redDeep">
                        <Banknote className="mr-1 h-4 w-4" />{triggerLabel}
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle>Record payment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono dark:border-zinc-800 dark:bg-zinc-900/40">
                        <div>Invoice <b>{invoice?.invoice_number}</b></div>
                        <div>Outstanding {inr(invoice?.amount_due_minor)}</div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Method</Label>
                            <Select value={method} onValueChange={setMethod}>
                                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Amount (paise)</Label>
                            <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="payment-amount-input" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Reference</Label>
                        <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI ref / cheque # / txn id" data-testid="payment-reference-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Notes</Label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payment-notes-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="payment-submit" className="bg-brand-red text-white hover:bg-brand-redDeep">
                        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                        Record {inr(Number(amount) || 0)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
